<template>
  <div class="page page-session">
    <div v-if="rec" class="rv">
      <div class="stage2">
        <section class="main-col">
          <div class="feature-head">
            <div class="fh-top">
              <span v-if="rec.tag" class="pill">{{ rec.tag }}</span>

              <!-- multiple renderable versions → switcher dropdown; otherwise a static badge -->
              <div v-if="vinfo.renderable.length > 1" ref="verSwitch" class="ver-switch">
                <button class="ver-badge" :class="activeVersion.kind" type="button" :aria-expanded="verOpen ? 'true' : 'false'" @click="verOpen = !verOpen">
                  {{ activeVersion.label }} · {{ activeVersion.id }}
                  <svg class="ver-caret" viewBox="0 0 24 24" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
                </button>
                <div v-show="verOpen" class="ver-menu" role="menu">
                  <button
                    v-for="v in vinfo.renderable"
                    :key="v.id"
                    class="ver-opt"
                    :class="{ on: v.id === activeVersion.id }"
                    type="button"
                    role="menuitemradio"
                    :aria-checked="v.id === activeVersion.id ? 'true' : 'false'"
                    @click="selectVer(v)"
                  >
                    <span class="ver-opt-top"><b>{{ v.label }}</b><span class="ver-opt-id">{{ v.id }}</span></span>
                    <span v-if="v.note" class="ver-opt-note">{{ v.note }}</span>
                  </button>
                </div>
              </div>
              <span v-else-if="vinfo.primary" class="ver-badge" :class="vinfo.primary.kind" :title="vinfo.primary.note || ''">
                {{ vinfo.primary.label }} · {{ vinfo.primary.id }}
              </span>

              <ContentCredentials v-if="provenance" :provenance="provenance" :files="provFiles" />
            </div>
            <h1 class="fh-title">{{ rec.title }}</h1>
            <p class="fh-meta">{{ metaLine }}</p>
          </div>

          <!-- grasp: what was assembled, felt before play -->
          <div v-if="grasp.length" class="rv-grasp">
            <span v-for="(g, i) in grasp" :key="i" class="ribbon-item"><b>{{ g.n }}</b><span>{{ g.label }}</span></span>
          </div>

          <div ref="playerWrap" class="player-wrap">
            <div class="frame-wrap">
              <div class="frame">
                <video
                  v-if="hasVideo"
                  ref="player"
                  controls
                  preload="metadata"
                  playsinline
                  poster=""
                  :src="arec.media.video.url"
                  aria-label="Recording"
                  @timeupdate="onTime"
                  @play="started = true"
                  @loadedmetadata="onMeta"
                ></video>
                <div v-else-if="hasAudio" class="audio-plate">
                  <span class="ring"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 18V5l11-2v13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" /><circle cx="6" cy="18" r="3" fill="currentColor" /><circle cx="20" cy="16" r="3" fill="currentColor" /></svg></span>
                  <span class="lab">Audio recording</span>
                  <span class="waveform"><i v-for="(h, i) in bars" :key="i" :style="{ height: h + 'px' }"></i></span>
                </div>

                <!-- pre-play plate (hides the composite 4-up poster) -->
                <button v-if="hasVideo && !started" class="player-poster" :style="posterStyle" aria-label="Play the recording" @click="startPlay">
                  <span class="play-badge"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 5v14l11-7z" /></svg></span>
                  <span v-if="rec.durationLabel" class="dur-chip">{{ rec.durationLabel }}</span>
                  <span class="pp-cap">{{ caption }}</span>
                </button>
              </div>
            </div>

            <audio
              v-if="!hasVideo && hasAudio"
              ref="player"
              controls
              preload="metadata"
              :src="arec.media.audio.url"
              @timeupdate="onTime"
              @loadedmetadata="onMeta"
            ></audio>

            <!-- feedback / one-time coaching (only occupies space when present) -->
            <div v-if="showJumped || coach" class="rv-underframe">
              <span v-if="showJumped" class="rv-jumped">Jumped to {{ jumpLabel }} ✓</span>
              <span v-else-if="coach" class="rv-coach">
                Click any line or insight to jump. The transcript follows.
                <button type="button" aria-label="Dismiss" @click="dismissCoach">✕</button>
              </span>
            </div>

          </div>

          <Transcript
            ref="transcript"
            :lines="arec.transcript || []"
            :current-time="curTime"
            :finding-secs="findingSecs"
            @seek="seek"
          />
        </section>

        <aside class="rail">
          <InsightsPanel
            :analysis="arec.analysis || {}"
            :rec="arec"
            :event-id="eventId"
            :rec-id="rec.id"
            :current-time="curTime"
            :show-cams="!vinfo.camsAreOriginals"
            :storage="event.storage || null"
            :force-tab="panelTab"
            :initial-qapo="panelQapo"
            :initial-sentiment="panelSentiment"
            @seek="seek"
          />
        </aside>
      </div>
    </div>

    <div v-else class="cover"><div class="cover-main">
      <p class="kicker">Recording</p>
      <h1 class="cover-title">This recording could not be found in the brief</h1>
      <router-link class="btn btn-ghost" :to="{ name: 'Event Hub', params: { id: eventId } }">Back to the brief</router-link>
    </div></div>
  </div>
</template>

<script>
import { versionInfo, posterFor, normalizeProvenance } from './useEvent';
import pageProps from './pages/pageProps';
import Transcript from './components/Transcript.vue';
import InsightsPanel from './components/InsightsPanel.vue';
import ContentCredentials from '@/components/credentials/ContentCredentials.vue';

export default {
  name: 'RecordingView',
  components: { Transcript, InsightsPanel, ContentCredentials },
  mixins: [pageProps],
  data() {
    return {
      curTime: 0,
      duration: 0,
      started: false,
      coach: false,
      showJumped: false,
      jumpLabel: '',
      selectedVer: null,
      verOpen: false,
      isMobile: false,
      bars: [6, 12, 20, 10, 16, 8, 18, 12, 6, 14, 9, 17, 11, 7, 10, 15],
    };
  },
  computed: {
    recId() {
      return this.$route.params.recId;
    },
    rec() {
      if (!this.recordings.length) return null;
      return this.recordings.find((r) => r.id === this.recId) || this.recordings[0];
    },
    // the version the viewer is currently looking at (default = current/primary)
    activeVersion() {
      const vi = this.vinfo;
      if (!vi.renderable.length) return null;
      return vi.renderable.find((v) => v.id === this.selectedVer) || vi.primary || vi.renderable[0];
    },
    // the recording as rendered for the active version: a non-current renderable
    // version may carry its own assets bundle; otherwise the top-level assets apply
    arec() {
      const r = this.rec;
      if (!r) return r;
      const a = this.activeVersion && this.activeVersion.assets;
      if (!a) return r;
      return {
        ...r,
        media: a.media || r.media,
        transcript: a.transcript || r.transcript,
        elements: a.elements || r.elements,
        analysis: a.analysis || r.analysis,
      };
    },
    hasVideo() {
      return !!(this.arec && this.arec.media && this.arec.media.video);
    },
    hasAudio() {
      return !!(this.arec && this.arec.media && this.arec.media.audio);
    },
    metaLine() {
      if (!this.rec) return '';
      return [this.rec.date, this.rec.startTime, this.rec.durationLabel, this.rec.tracks].filter(Boolean).join('  ·  ');
    },
    caption() {
      if (!this.rec) return '';
      return [this.rec.tag, this.rec.durationLabel, this.rec.tracks].filter(Boolean).join(' · ');
    },
    posterStyle() {
      const p = posterFor(this.rec);
      return p ? { backgroundImage: `url("${p}")` } : {};
    },
    findings() {
      return (this.arec && this.arec.analysis && this.arec.analysis.findings) || [];
    },
    vinfo() {
      return versionInfo(this.rec);
    },
    // hub deep-link (?panel=insights&qapo=Q | &sentiment=positive) → open pre-filtered
    panelTab() {
      return this.$route.query.panel === 'insights' ? 'insights' : '';
    },
    panelQapo() {
      const q = this.$route.query.qapo;
      return ['Q', 'A', 'P', 'O'].indexOf(q) >= 0 ? q : null;
    },
    panelSentiment() {
      const s = String(this.$route.query.sentiment || '').toLowerCase();
      return ['positive', 'neutral', 'negative', 'mixed'].indexOf(s) >= 0 ? s : null;
    },
    provenance() {
      const p = normalizeProvenance(this.arec);
      // the credential describes the version currently rendered - so AI-edited must
      // follow the active version (optimized = AI-edited yes; original = no)
      if (p && this.activeVersion) p.aiDerived = this.activeVersion.kind === 'optimized';
      return p;
    },
    provFiles() {
      const m = (this.arec && this.arec.media) || {};
      const out = [];
      if (m.video) out.push('Main video');
      if (m.audio) out.push('Audio');
      if (m.transcriptVtt || (this.arec && (this.arec.transcript || []).length)) out.push('Transcript');
      return out;
    },
    findingSecs() {
      return this.findings.map((f) => f.seconds).filter((n) => typeof n === 'number');
    },
    grasp() {
      if (!this.arec) return [];
      const els = this.arec.elements || {};
      const items = [
        { n: (els.clips || []).length, label: 'highlights' },
        { n: this.findings.length, label: 'insights' },
        { n: (this.arec.transcript || []).length, label: 'transcript lines' },
      ];
      return items.filter((x) => x.n > 0);
    },
  },
  created() {
    this.$nextTick(this.applyDeepLink);
    this.setTitle();
    try {
      this.coach = !localStorage.getItem('decast_coupling_hint_seen');
    } catch (e) {
      this.coach = false;
    }
  },
  mounted() {
    if (typeof window !== 'undefined' && window.matchMedia) {
      this._mq = window.matchMedia('(max-width: 960px)');
      this.isMobile = this._mq.matches;
      this._mqh = (e) => {
        this.isMobile = e.matches;
      };
      if (this._mq.addEventListener) this._mq.addEventListener('change', this._mqh);
      else if (this._mq.addListener) this._mq.addListener(this._mqh);
    }
    // close the version menu on outside click / Escape
    this._verDoc = (e) => {
      if (this.verOpen && this.$refs.verSwitch && !this.$refs.verSwitch.contains(e.target)) this.verOpen = false;
    };
    this._verKey = (e) => {
      if (e.key === 'Escape') this.verOpen = false;
    };
    document.addEventListener('click', this._verDoc);
    document.addEventListener('keydown', this._verKey);
  },
  watch: {
    recId() {
      this.curTime = 0;
      this.duration = 0;
      this.started = false;
      this.selectedVer = null;
      this.verOpen = false;
      this.setTitle();
      this.$nextTick(this.applyDeepLink);
    },
    // arriving via a hub deep-link on mobile: bring the insights panel into view
    rec(v) {
      if (v && this.panelTab && this.isMobile) {
        this.$nextTick(() => {
          const el = this.$el.querySelector('.ip');
          if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 200);
        });
      }
    },
  },
  methods: {
    setTitle() {
      const title = this.rec ? this.rec.title : this.event && this.event.title;
      if (title) document.title = `${title} · Decast`;
    },
    onTime(e) {
      this.curTime = e.target.currentTime || 0;
    },
    onMeta(e) {
      this.duration = e.target.duration || 0;
    },
    startPlay() {
      this.started = true;
      this.$nextTick(() => {
        const p = this.$refs.player;
        if (p && p.play) {
          const pl = p.play();
          if (pl && pl.catch) pl.catch(() => {});
        }
      });
    },
    seekTo(sec, autoplay) {
      const p = this.$refs.player;
      if (!p) return;
      const s = Math.max(0, sec || 0);
      const go = () => {
        try {
          p.currentTime = s;
        } catch (e) {
          /* not ready */
        }
        if (autoplay) {
          const pl = p.play();
          if (pl && pl.catch) pl.catch(() => {});
        }
        if (this.$refs.transcript) this.$refs.transcript.scrollToSec(s);
      };
      if (p.readyState >= 1) go();
      else p.addEventListener('loadedmetadata', go, { once: true });
    },
    // one-gesture coupling: any finding/insight/highlight/transcript click lands here
    seek(sec) {
      this.started = true;
      this.seekTo(sec, true);
      if (this.coach) this.dismissCoach();
      this.jumpLabel = this.clock(sec);
      this.showJumped = true;
      clearTimeout(this._jumpT);
      this._jumpT = setTimeout(() => {
        this.showJumped = false;
      }, 1300);
      // on mobile the player may be scrolled off - bring it back so the jump is visible
      if (this.isMobile && this.$refs.playerWrap && this.$refs.playerWrap.scrollIntoView) {
        this.$refs.playerWrap.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },
    selectVer(v) {
      this.verOpen = false;
      if (this.activeVersion && v.id === this.activeVersion.id) return;
      this.selectedVer = v.id;
      this.curTime = 0;
      this.started = false;
    },
    applyDeepLink() {
      const t = parseFloat(this.$route.query.t);
      if (!isNaN(t) && t > 0) {
        this.started = true;
        this.seekTo(t, false);
      }
    },
    dismissCoach() {
      this.coach = false;
      try {
        localStorage.setItem('decast_coupling_hint_seen', '1');
      } catch (e) {
        /* ignore */
      }
    },
    pct(sec) {
      if (!this.duration) return '0%';
      return `${Math.max(0, Math.min(100, (sec / this.duration) * 100))}%`;
    },
    clock(sec) {
      sec = Math.max(0, Math.floor(sec || 0));
      const h = Math.floor(sec / 3600);
      const m = Math.floor((sec % 3600) / 60);
      const s = sec % 60;
      const pad = (n) => (n < 10 ? '0' + n : '' + n);
      return h ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
    },
    typeLabel(t) {
      return String(t || '').replace(/_/g, ' ');
    },
  },
  beforeDestroy() {
    clearTimeout(this._jumpT);
    if (this._mq) {
      if (this._mq.removeEventListener) this._mq.removeEventListener('change', this._mqh);
      else if (this._mq.removeListener) this._mq.removeListener(this._mqh);
    }
    document.removeEventListener('click', this._verDoc);
    document.removeEventListener('keydown', this._verKey);
  },
};
</script>

<style lang="scss" src="./styles/event-hub.scss"></style>
